import React, { useState, useEffect } from 'react';
import { Users, Image, TrendingUp } from 'lucide-react';

const PublicStats = () => {
  const [stats, setStats] = useState({
    total_users: 0,
    total_carousels: 0,
    loading: true
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Use environment variable for API URL, fallback to localhost for development
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const response = await fetch(`${apiUrl}/ai/public/stats/`);
        
        if (response.ok) {
          const data = await response.json();
          setStats({
            total_users: data.total_users,
            total_carousels: data.total_carousels,
            loading: false
          });
        } else {
          console.error('Failed to fetch stats');
          setStats(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        setStats(prev => ({ ...prev, loading: false }));
      }
    };

    fetchStats();
  }, []);

  // Animated counter effect
  const AnimatedNumber = ({ value, duration = 2000 }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
      if (value === 0) return;

      const startTime = Date.now();
      const endValue = value;

      const updateValue = () => {
        const now = Date.now();
        const progress = Math.min((now - startTime) / duration, 1);
        
        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = Math.floor(easeOutQuart * endValue);
        
        setDisplayValue(currentValue);

        if (progress < 1) {
          requestAnimationFrame(updateValue);
        } else {
          setDisplayValue(endValue);
        }
      };

      requestAnimationFrame(updateValue);
    }, [value, duration]);

    return <span>{displayValue.toLocaleString()}</span>;
  };

  if (stats.loading) {
    return (
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-16 bg-white/20 rounded-lg mb-2"></div>
                <div className="h-4 bg-white/20 rounded w-32 mx-auto"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-2">
            Join Our Growing Community
          </h2>
          <p className="text-blue-100 text-lg">
            Trusted by creators worldwide
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Total Users */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center transform hover:scale-105 transition-all duration-300 border border-white/20 shadow-xl">
            <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-white" />
            </div>
            <div className="text-5xl font-bold text-white mb-2">
              <AnimatedNumber value={stats.total_users} />
              <span className="text-blue-200">+</span>
            </div>
            <p className="text-blue-100 text-lg font-medium">Happy Users</p>
            <p className="text-blue-200 text-sm mt-2">Creating amazing content</p>
          </div>

          {/* Total Carousels */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center transform hover:scale-105 transition-all duration-300 border border-white/20 shadow-xl">
            <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Image className="h-8 w-8 text-white" />
            </div>
            <div className="text-5xl font-bold text-white mb-2">
              <AnimatedNumber value={stats.total_carousels} />
              <span className="text-purple-200">+</span>
            </div>
            <p className="text-blue-100 text-lg font-medium">Carousels Created</p>
            <p className="text-blue-200 text-sm mt-2">And counting every day</p>
          </div>

          {/* Average Carousels per User */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center transform hover:scale-105 transition-all duration-300 border border-white/20 shadow-xl">
            <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="h-8 w-8 text-white" />
            </div>
            <div className="text-5xl font-bold text-white mb-2">
              <AnimatedNumber 
                value={stats.total_users > 0 ? Math.round((stats.total_carousels / stats.total_users) * 10) / 10 : 0} 
              />
            </div>
            <p className="text-blue-100 text-lg font-medium">Avg. per User</p>
            <p className="text-blue-200 text-sm mt-2">Engagement rate</p>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 text-center">
          <div className="flex flex-wrap justify-center items-center gap-8 text-white/80">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm">Live Stats</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">Trusted Platform</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="text-sm">5-Star Rated</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicStats;
